import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PROVIDER_MODEL_KEYS, PROVIDER_MODEL_LABELS } from "@shared/provider-routing";
import type { Provider, ProviderModelKey } from "@shared/provider-routing";
import { apiRequest } from "@/lib/queryClient";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { KieThrottleCard } from "@/components/admin/kie-throttle-card";

const settingsKey = ["/api/admin/provider-routing"];

type ProviderSettingValue = {
  value: Provider | "on" | "off";
  updatedBy: string | null;
  updatedAt: string | null;
  version: number;
  editor: string;
};

type ProviderRoutingResponse = {
  models: Record<ProviderModelKey, ProviderSettingValue>;
  forceFal: ProviderSettingValue;
};

function providerName(provider: Provider) {
  return provider === "fal" ? "FAL" : "KIE";
}

function changedAt(setting: ProviderSettingValue) {
  return setting.updatedAt ? new Date(setting.updatedAt).toLocaleString() : "Not recorded";
}

export function ProviderRoutingSetting() {
  const client = useQueryClient();
  const { toast } = useToast();
  const [drafts, setDrafts] = useState<Partial<Record<ProviderModelKey, Provider>>>({});
  const [pendingChange, setPendingChange] = useState<{ kind: "force"; value: boolean } | { kind: "model"; key: ProviderModelKey; value: Provider } | null>(null);
  const query = useQuery<ProviderRoutingResponse>({
    queryKey: settingsKey,
    queryFn: async () => (await apiRequest("GET", settingsKey[0])).json(),
    refetchInterval: 15000,
  });
  const save = useMutation({
    mutationFn: async (change: { kind: "force"; value: boolean } | { kind: "model"; key: ProviderModelKey; value: Provider }) => {
      const body = change.kind === "force"
        ? { forceFal: change.value }
        : { modelKey: change.key, value: change.value };
      return (await apiRequest("PUT", settingsKey[0], body)).json();
    },
    onSuccess: async (_saved, change) => {
      if (change.kind === "model") {
        setDrafts((current) => {
          const next = { ...current };
          delete next[change.key];
          return next;
        });
      }
      setPendingChange(null);
      toast({ title: change.kind === "force" ? "Provider override saved" : "Provider selection saved" });
      await client.invalidateQueries({ queryKey: settingsKey });
    },
    onError: (_error, change) => {
      toast({
        title: change.kind === "force" ? "Could not save provider override" : "Could not save provider selection",
        variant: "destructive",
      });
    },
  });

  const forceOn = query.data?.forceFal.value === "on";
  const confirmPending = () => {
    if (pendingChange) save.mutate(pendingChange);
  };

  return (
    <div className="space-y-5">
      <Card className="border-red-300 bg-red-50/70">
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="text-red-950">Force ALL to FAL</CardTitle>
            {query.data && <Badge variant="outline" className="border-red-300 bg-red-100 text-red-900">
              {forceOn ? "ON" : "OFF"}
            </Badge>}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-red-950">When ON, every selector below is ignored and all new renders go to FAL.</p>
          {query.data && <p className="text-xs text-red-900/80">
            Last changed by {query.data.forceFal.editor || query.data.forceFal.updatedBy || "Not recorded"} · {changedAt(query.data.forceFal)}
          </p>}
          <Button
            variant={forceOn ? "outline" : "destructive"}
            disabled={!query.data || query.isError || save.isPending}
            data-testid="provider-routing-force-fal"
            onClick={() => setPendingChange({ kind: "force", value: !forceOn })}
          >
            {forceOn ? "Turn override off" : "Turn override on"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Provider routing</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Changes apply to NEW renders only. Renders already submitted finish on the provider they started on.
          </p>
          {query.isLoading ? (
            <div aria-label="Loading provider routing" className="space-y-3" data-testid="provider-routing-loading">
              {[0, 1, 2, 3].map((row) => <div key={row} className="h-14 animate-pulse rounded-md bg-muted" />)}
            </div>
          ) : query.isError ? (
            <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm">
              <p>Could not load provider routing settings.</p>
              <Button className="mt-3" variant="outline" onClick={() => void query.refetch()}>Retry</Button>
            </div>
          ) : query.data ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[680px] border-collapse text-left text-sm">
                <thead>
                  <tr className="border-b text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="py-3 pr-4 font-medium">Model</th>
                    <th className="py-3 pr-4 font-medium">Selected provider</th>
                    <th className="py-3 pr-4 font-medium">Effective provider</th>
                    <th className="py-3 font-medium">Last changed</th>
                  </tr>
                </thead>
                <tbody>
                  {PROVIDER_MODEL_KEYS.map((key) => {
                    const setting = query.data!.models[key];
                    const selected = drafts[key] ?? setting.value as Provider;
                    return (
                      <tr key={key} className="border-b last:border-0">
                        <th scope="row" className="py-4 pr-4 font-medium">{PROVIDER_MODEL_LABELS[key]}</th>
                        <td className="py-4 pr-4">
                          <div className="inline-flex rounded-md border bg-muted/40 p-1" role="group" aria-label={`${PROVIDER_MODEL_LABELS[key]} provider`} data-testid={`provider-routing-select-${key}`}>
                            {(["fal", "kie"] as const).map((provider) => (
                              <button
                                key={provider}
                                type="button"
                                aria-pressed={selected === provider}
                                disabled={save.isPending}
                                onClick={() => setDrafts((current) => ({ ...current, [key]: provider }))}
                                className={`rounded px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 ${selected === provider ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                              >
                                {providerName(provider)}
                              </button>
                            ))}
                          </div>
                          <Button
                            size="sm"
                            className="ml-2"
                            disabled={save.isPending || selected === setting.value}
                            data-testid={`provider-routing-save-${key}`}
                            onClick={() => {
                              const change = { kind: "model" as const, key, value: selected };
                              if (setting.value === "fal" && selected === "kie") setPendingChange(change);
                              else save.mutate(change);
                            }}
                          >
                            Save
                          </Button>
                        </td>
                        <td className="py-4 pr-4">
                          <span>{providerName(forceOn ? "fal" : setting.value as Provider)}</span>
                          {forceOn && <Badge variant="outline" className="ml-2 border-red-300 bg-red-50 text-red-800">forced</Badge>}
                        </td>
                        <td className="py-4 text-xs text-muted-foreground">
                          {setting.editor || setting.updatedBy || "Not recorded"}<br />
                          {changedAt(setting)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <KieThrottleCard />

      <AlertDialog open={pendingChange !== null} onOpenChange={(open) => !open && !save.isPending && setPendingChange(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingChange?.kind === "force"
                ? `Turn Force ALL to FAL ${pendingChange.value ? "on" : "off"}?`
                : "Change provider to KIE?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingChange?.kind === "force"
                ? pendingChange.value
                  ? "All new renders will use FAL regardless of the selectors below."
                  : "New renders will use the saved provider selectors again."
                : `New ${pendingChange ? PROVIDER_MODEL_LABELS[pendingChange.key] : "model"} renders will use KIE. Existing renders are unchanged.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={save.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={save.isPending} onClick={(event) => {
              event.preventDefault();
              confirmPending();
            }}>
              {save.isPending ? "Saving..." : "Confirm change"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
